import { useMemo } from 'react';
import { Property } from '../../types/user';
import { Card } from '../ui/Card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { formatCurrency } from '../../utils/format';

interface GlobalStatsProps {
  properties: Property[];
}

const COLORS = ['#ef4444', '#22c55e', '#3b82f6', '#f59e0b'];

export const GlobalStats = ({ properties }: GlobalStatsProps) => {
  const stats = useMemo(() => {
    return properties.reduce((acc, property) => ({
      revenue: acc.revenue + property.stats.revenue,
      cleaningCosts: acc.cleaningCosts + property.stats.cleaningCosts,
      inmohostCosts: acc.inmohostCosts + property.stats.inmohostCosts,
      profit: acc.profit + property.stats.profit,
      occupancyRate: acc.occupancyRate + property.stats.occupancyRate,
      reservations: acc.reservations + property.stats.reservations,
      averageNightPrice: acc.averageNightPrice + property.stats.averageNightPrice,
      averageStayDuration: acc.averageStayDuration + property.stats.averageStayDuration
    }), {
      revenue: 0,
      cleaningCosts: 0,
      inmohostCosts: 0,
      profit: 0,
      occupancyRate: 0,
      reservations: 0,
      averageNightPrice: 0,
      averageStayDuration: 0
    });
  }, [properties]);

  const averages = useMemo(() => ({
    occupancyRate: Math.round(stats.occupancyRate / properties.length),
    nightPrice: Math.round(stats.averageNightPrice / properties.length),
    stayDuration: Math.round(stats.averageStayDuration / properties.length)
  }), [stats, properties.length]);

  const propertyComparison = properties.map(property => ({
    name: property.name,
    ingresos: property.stats.revenue,
    beneficio: property.stats.profit,
    ocupacion: property.stats.occupancyRate
  }));

  const distributionData = [
    { name: 'Beneficio', value: stats.profit },
    { name: 'Gastos Limpieza', value: stats.cleaningCosts },
    { name: 'Gastos INMOHOST', value: stats.inmohostCosts }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900">Facturación Total</h3>
            <p className="mt-2 text-3xl font-bold text-red-600">{formatCurrency(stats.revenue)}</p>
            <p className="mt-1 text-sm text-gray-500">
              Media por propiedad: {formatCurrency(stats.revenue / properties.length)}
            </p>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900">Beneficio Total</h3>
            <p className="mt-2 text-3xl font-bold text-green-600">{formatCurrency(stats.profit)}</p>
            <p className="mt-1 text-sm text-gray-500">
              Margen: {Math.round((stats.profit / stats.revenue) * 100)}%
            </p>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900">Ocupación Media</h3>
            <p className="mt-2 text-3xl font-bold text-blue-600">{averages.occupancyRate}%</p>
            <p className="mt-1 text-sm text-gray-500">
              Total reservas: {stats.reservations}
            </p>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900">Precio Medio/Noche</h3>
            <p className="mt-2 text-3xl font-bold text-yellow-600">{formatCurrency(averages.nightPrice)}</p>
            <p className="mt-1 text-sm text-gray-500">
              Estancia media: {averages.stayDuration} noches
            </p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Comparativa de Propiedades</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={propertyComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="ingresos" name="Ingresos" fill="#ef4444" />
                <Bar dataKey="beneficio" name="Beneficio" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Distribución Global de Ingresos</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={distributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {distributionData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Métricas por Propiedad</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Propiedad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Facturación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Beneficio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ocupación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Precio/Noche
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {properties.map((property) => (
                <tr key={property.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{property.name}</div>
                    <div className="text-sm text-gray-500">{property.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(property.stats.revenue)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(property.stats.profit)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {Math.round((property.stats.profit / property.stats.revenue) * 100)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{property.stats.occupancyRate}%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(property.stats.averageNightPrice)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};